import { filterDOMProps } from '@react-aria/utils';
import { cva } from '@vizit/theme';
import * as React from 'react';
import { Text, TextSize, TextWeight } from '../text';
import { PillLevel } from './types';

interface PillProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The display level of the pill.
   *
   * @optional
   * @type 'default' | 'information' | 'success' | 'warning' | 'error'
   * @default 'default'
   */
  level?: PillLevel;
  /**
   * The ref to use for the pill.
   *
   * @optional
   * @type React.Ref<HTMLDivElement>
   * @default undefined
   */
  ref?: React.Ref<HTMLDivElement>;
}

const classes = cva({
  base: 'inline-flex items-center justify-center px-4 py-1 min-w-24',
  variants: {
    level: {
      [PillLevel.Default]: 'text-secondary border border-disabled bg-secondary',
      [PillLevel.Information]: 'text-orange-700 border border-orange-200 bg-orange-50',
      [PillLevel.Success]: 'text-green-primary border border-success bg-success-solid text-success-primary',
      [PillLevel.Warning]: 'text-starburst-primary border border-warning bg-warning-solid text-warning-primary',
      [PillLevel.Error]: 'text-red-primary bg-error-solid text-error-primary-hover border border-error',
    },
  },
  defaultVariants: {
    level: PillLevel.Default,
  },
});

/**
 * A pill to display a status or label.
 *
 * @param {PillProps} - The props to use for the pill.
 */
function Pill(props: PillProps) {
  const { ref, children, className, level = PillLevel.Default, ...other } = props;

  const isTextOnly = React.Children.toArray(props.children).every((c) => !React.isValidElement(c));

  return (
    <div {...filterDOMProps(other)} ref={ref} className={classes({ className, level })} role="presentation">
      {typeof children === 'string' || isTextOnly ? (
        <Text elementType="span" weight={TextWeight.Regular} size={TextSize.Xs} className={'text-inherit'}>
          {children}
        </Text>
      ) : (
        children
      )}
    </div>
  );
}

export type { PillProps };
export { Pill };
